import { useLatestVisitorsColumnsV8 } from '@va/dashboard/shared/visitors';
import { useFiltersContext } from '@va/shared/feature-filters';
import { DataTableV8, TableSorting, useControlledTableState } from '@va/ui/components/data-table';
import { useReportBlockCtx } from '@va/ui/components/report-block';
import { useRefreshCallbacks, useRefreshContext } from '@va/util/components';
import { memo, useEffect, useState } from 'react';
import { LatestVisitorsItemV2, useLatestVisitors } from './useLatestVisitors';

export const LatestVisitorsTable = memo(
  ({
    disablePagination,
    initialPageSize,
    displayTableHeader,
    defaultSorting,
  }: {
    disablePagination?: boolean;
    initialPageSize?: number;
    displayTableHeader?: boolean;
    defaultSorting?: TableSorting<Extract<keyof LatestVisitorsItemV2, string>>[];
  }) => {
    const { id, paginationId } = useReportBlockCtx();
    const { createApiConditions } = useFiltersContext();
    const { pagination, setPagination, sorting, setSorting } = useControlledTableState(
      {
        pagination: { pageSize: initialPageSize },
        defaultSorting: defaultSorting,
      },
      id,
    );

    const { data, isLoading, mutate } = useLatestVisitors({
      pagination: { page: pagination.pageNumber, pageSize: pagination.pageSize },
      order: sorting,
      where: { and: createApiConditions() },
    });

    const [rowCount, setRowCount] = useState(data?.meta?.total ?? 0);

    useEffect(() => {
      setRowCount((prev) => {
        if (isLoading) return prev;
        if (!data) return prev;
        return data?.meta?.total;
      });
    }, [isLoading, data]);

    const columnsV8 = useLatestVisitorsColumnsV8();
    const { isManualRefreshing } = useRefreshContext();
    const isInProgress = isManualRefreshing || isLoading;

    useRefreshCallbacks(mutate);

    return (
      <DataTableV8
        id='latest-visitors'
        className='lg:min-w-[75rem]'
        columns={columnsV8}
        isLoading={isInProgress}
        data={data?.payload ?? []}
        rowCount={rowCount}
        state={{ pagination, sorting }}
        disablePagination={disablePagination}
        paginationContainerId={disablePagination ? undefined : paginationId}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        displayTableHeader={displayTableHeader}
        defaultSorting={defaultSorting}
      />
    );
  },
);
